import { Images } from "../../../Images";
import BigNum from "../../../model/math/BigNum";
import ClassicUpgrade from "../../../model/upgrade/classic/ClassicUpgrade";
import { CountToPurchase } from "../../../model/upgrade/classic/CountToPurchase";
import NumberFormatter from "../../../util/NumberFormatter";
import Page from "../../framework/Page";
import View from "../../framework/View";

export default class ClassicUpgradeView extends View {
    /**
     * @param {string} parentId 
     * @param {string} elementId 
     * @param {ClassicUpgrade} upgrade
     * @param {boolean} displayDescriptionRow
     */
    constructor(parentId, elementId, upgrade, displayDescriptionRow) {
        super(parentId, elementId);

        /**
         * @type {ClassicUpgrade}
         */
        this.upgrade = upgrade;

        /**
         * @constant
         * @type {boolean}
         */
        this.displayDescriptionRow = displayDescriptionRow;

        /**
         * @type {HTMLElement}
         */
        this.mainElement = null;

        /**
         * @type {HTMLElement}
         */
        this.titleElement = null;

        /**
         * @type {HTMLElement}
         */
        this.descriptionElement = null;

        /**
         * @type {HTMLElement}
         */
        this.costElement = null;

        /**
         * @type {BigNum}
         */
        this.displayedCost = new BigNum(-10);

        /**
         * @type {HTMLElement}
         */
        this.countElement = null;

        /**
         * @type {number}
         */
        this.displayedCount = -10;
        /**
         * @type {number}
         */
        this.displayedNextActualPurchaseCount = -10;

        /**
         * @type {string}
         */
        this.displayedTitle = null;

        /**
         * @type {string}
         */
        this.displayedDescription = null;

        /**
         * @type {boolean}
         */
        this.displayedPurchasable = !this.upgrade.isPurchasable();
    }

    /**
     * @param {ClassicUpgrade} upgrade 
     */
    setClassicUpgrade(upgrade) {
        this.upgrade = upgrade;
    }

    /**
     * @override
     */
    initializeView() {
        // The upgrade view is dynamically created.
        const id = this.getElementId();

        const parent = Page.getElement(this.parentId);
        if (!parent) {
            console.log('PickUpgradeView.initializeView() No parent element found! parentId=' + this.parentId);
            return;
        }

        this.displayedPurchasable = this.upgrade.isPurchasable();
        const mainElementClass = this.displayedPurchasable ? 'upgrade-button-outer' : 'upgrade-button-outer-not-purchasable';

        this.mainElement = Page.createElement('div', parent, id, mainElementClass);

        const row1 = Page.createElement('div', this.mainElement, null, 'pick-upgrade-button-row1');
        // const row2 = Page.createElement('div', this.mainElement, null, 'pick-upgrade-button-row2');

        ////////////////////////////////////////////////////////
        // Row:
        // [icon] [description] [count/cost]
        ////////////////////////////////////////////////////////

        // <img src="images/miner_icon_51x48.png"/>
        const imgElement = Page.createElement('img', row1, null, 'pick-upgrade-icon');
        imgElement.src = Images.pick;

        // Title/Description container
        const titleDescriptionContainer = Page.createElement('div', row1, null, 'pick-upgrade-title-description-container');

        if (this.displayDescriptionRow) {
            this.titleElement = Page.createElement('div', titleDescriptionContainer, null, 'pick-upgrade-button-title-double-row');

            this.descriptionElement = Page.createElement('div', titleDescriptionContainer, null, 'pick-upgrade-button-description');
            this.descriptionElement.innerHTML = '';
        }
        else {
            this.titleElement = Page.createElement('div', titleDescriptionContainer, null, 'pick-upgrade-button-title-single-row');
        }

        this.titleElement.innerHTML = '';

        ////////////////////////////////////////////////////////
        // Right Element (count above, cost below)
        ////////////////////////////////////////////////////////

        const rightElement = Page.createElement('div', row1, null, null);

        this.countElement = Page.createElement('div', rightElement, null, 'pick-upgrade-count');

        this.costElement = Page.createElement('div', rightElement, null, 'pick-upgrade-cost');


        //////////////////////////////////////
        // Setup initial visibility
        //////////////////////////////////////

        this.setInitialVisibility(this.upgrade.isVisible());

        //////////////////////////////////////
        // Button Click
        //////////////////////////////////////

        const thisRef = this;
        this.mainElement.onclick = function() {
            if (!thisRef.upgrade.isPurchasable()) {
                return;
            }

            thisRef.upgrade.purchase();
        };

        super.initializeView();
    }

    /**
     * Updates the user interface to reflect the data in the model.
     * @override
     */
    updateView() {
        // We need to override this method to update the upgrade visibility
        this.setViewVisible(this.upgrade && this.upgrade.isVisible());

        super.updateView();
    }

    /**
     * @override
     */
    updateViewContents() {

        const cost = this.upgrade.getCost();
        const count = this.upgrade.purchasedCount;
        const purchasable = this.upgrade.isPurchasable();

        const countToPurchase = this.upgrade.getCountToPurchaseSetting();

        const nextActualPurchaseCount = this.upgrade.nextActualPurchaseCount;
        
        if (!cost.equals(this.displayedCost)) {
            this.displayedCost.copy(cost);
            this.costElement.innerHTML = NumberFormatter.formatBigNum(cost) + ' ' + this.upgrade.getCostUnit();
        }

        if (count !== this.displayedCount || nextActualPurchaseCount !== this.displayedNextActualPurchaseCount) {
            this.displayedCount = count;
            this.displayedNextActualPurchaseCount = nextActualPurchaseCount;

            if (countToPurchase === CountToPurchase.purchaseOne) {
                this.countElement.innerHTML = '' + count;
            }
            else {
                this.countElement.innerHTML = '' + count + '+' + nextActualPurchaseCount;
            }
        }

        const title = this.upgrade.title;
        if (title !== this.displayedTitle) {
            this.displayedTitle = title;
            this.titleElement.innerHTML = title;
        }

        if (this.displayDescriptionRow) {
            const description = this.upgrade.getDescription();
            if (description && description !== this.displayedDescription) {
                this.displayedDescription = description;
                this.descriptionElement.innerHTML = description;
            }
        }

        if (purchasable !== this.displayedPurchasable) {
            this.displayedPurchasable = purchasable;

            if (purchasable) {
                this.mainElement.className = 'upgrade-button-outer';
            }
            else {
                this.mainElement.className = 'upgrade-button-outer-not-purchasable';
            }
        }
    }
}